import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Add } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import { BinType, InboundTaskItemStatusEnum } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import { IBin, IInboundTaskProcessedItem } from 'commons/types';
import {
  actionPostInboundTaskRemoveBatch,
  actionPostReceiveActual,
  getValidateBin,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import {
  setInboundTaskcurrentReceiveItemIndex,
  setInboundTaskReceiveItem,
} from 'redux-stores/reducers/inboundTaskItemReducer';
import { AppDispatch } from 'redux-stores/store';
import { INBOUND_ROUTES } from 'routes/constants';

import ReceiveProcessForm from './components/ReceiveProcessForm.component';
import S from './InboundItemReceivePage.style';

const EMPTY_ACTUAL = {
  bin: '',
  bin_type: BinType.NONE,
  inventory_number: '',
  expiry_date: new Date().toISOString(),
  quantity: 0,
};

export const InboundItemReceivePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    inboundTaskReceiveItem,
    currentReceiveItemIndex,
    assignedInboundTaskItems,
  } = useSelector((state: RootReducerInterface) => state.inboundTaskItem);

  const [colorProgress, setColorProgress] = useState(colors.PRIMARY_RED);
  const [actuals, setActuals] = useState<IInboundTaskProcessedItem[]>(
    structuredClone(inboundTaskReceiveItem?.receive_actual || []),
  );
  const [receivedQty, setReceivedQty] = useState(0);
  useEffect(() => {
    if (
      inboundTaskReceiveItem &&
      inboundTaskReceiveItem.receive_actual &&
      inboundTaskReceiveItem.receive_actual.length > 0
    ) {
      // set actual data
      setActuals(structuredClone(inboundTaskReceiveItem.receive_actual));
      const totalReceivedQty = inboundTaskReceiveItem.receive_actual.reduce(
        (total, dest) => total + dest.quantity,
        0,
      );
      // set current progress actual quantity has been processed
      setReceivedQty(totalReceivedQty);

      setColorProgress(
        totalReceivedQty === 0
          ? colors.PRIMARY_RED
          : totalReceivedQty === inboundTaskReceiveItem.quantity
          ? colors.PRIMARY_GREEN
          : colors.PRIMARY_YELLOW,
      );
    } else {
      setActuals([EMPTY_ACTUAL]);
      setReceivedQty(0);
      setColorProgress(colors.PRIMARY_RED);
    }
  }, [inboundTaskReceiveItem]);

  /** DEBOUNCE FUNCTIONS */
  const debouncePostInboundTaskReceiveActual = useDebouncedCallback(
    (receiveActual: IInboundTaskProcessedItem) => {
      const skuCode = inboundTaskReceiveItem?.sku_code || '';
      const inboundTaskNumber =
        inboundTaskReceiveItem?.inbound_task_number || '';
      const foc = inboundTaskReceiveItem?.foc || false;
      dispatch(
        actionPostReceiveActual({
          skuCode,
          inboundTaskNumber,
          foc,
          receiveActual: {
            ...receiveActual,
            expiry_date: new Date(receiveActual.expiry_date),
          },
        }),
      );
    },
    500,
    { maxWait: 2000 },
  );

  const debouncePostInboundTaskRemoveBatch = useDebouncedCallback(
    (removedActual: IInboundTaskProcessedItem) => {
      const skuCode = inboundTaskReceiveItem?.sku_code || '';
      const inboundTaskNumber =
        inboundTaskReceiveItem?.inbound_task_number || '';
      const foc = inboundTaskReceiveItem?.foc || false;
      dispatch(
        actionPostInboundTaskRemoveBatch({
          skuCode,
          inboundTaskNumber,
          foc,
          removedActual,
        }),
      );
    },
    500,
    { maxWait: 2000 },
  );

  // early return
  if (!inboundTaskReceiveItem) {
    navigate(INBOUND_ROUTES.LIST);
    return <div />;
  }

  /** HANDLE FUNCTIONS */
  const handleVerifyBin = async (params: {
    bin: string;
    index: number;
  }): Promise<Pick<IBin, 'name' | 'bin_type'>> => {
    const result = await getValidateBin({ bin: params.bin }, dispatch);
    if (!result) {
      return { name: '', bin_type: '' };
    }
    return { name: result.name, bin_type: result.bin_type };
  };

  const handleAddBatch = (): void => {
    const newReceiveActuals = structuredClone(actuals);
    newReceiveActuals.push(EMPTY_ACTUAL);
    setActuals(newReceiveActuals);
  };

  const handleRemoveBatch = (): void => {
    const newReceiveActuals = structuredClone(actuals);
    const removedActual = newReceiveActuals.pop();

    if (removedActual && removedActual.bin && removedActual.inventory_number) {
      debouncePostInboundTaskRemoveBatch(removedActual);
    }
    const totalReceivedQty = newReceiveActuals.reduce(
      (total, dest) => total + (dest.quantity || 0),
      0,
    );

    setActuals(newReceiveActuals);
    setReceivedQty(totalReceivedQty);
  };

  const handleRemoveReceiveActual = (
    index: number,
    newItem: IInboundTaskProcessedItem,
  ): IInboundTaskProcessedItem => {
    const newReceiveActuals = structuredClone(actuals);
    const removedActual = newReceiveActuals[index];

    if (
      removedActual &&
      removedActual.bin &&
      removedActual.inventory_number &&
      removedActual.quantity
    ) {
      debouncePostInboundTaskRemoveBatch(removedActual);
    }

    newReceiveActuals[index] = newItem;

    const totalReceivedQty = newReceiveActuals.reduce(
      (total, dest) => total + (dest.quantity || 0),
      0,
    );
    setActuals(newReceiveActuals);
    setReceivedQty(totalReceivedQty);
    return newItem;
  };

  const handleUpdateReceiveActual = (
    index: number,
    inputtedReceiveActual: IInboundTaskProcessedItem,
  ): void => {
    const {
      bin,
      inventory_number: inventoryNumber,
      expiry_date: expiryDate,
      quantity,
    } = inputtedReceiveActual;
    const currentReceiveActual = actuals[index];
    if (!currentReceiveActual) {
      return;
    }
    if (
      bin === currentReceiveActual.bin &&
      inventoryNumber === currentReceiveActual.inventory_number &&
      currentReceiveActual.expiry_date &&
      DateTime.fromJSDate(new Date(expiryDate)).hasSame(
        DateTime.fromJSDate(new Date(currentReceiveActual.expiry_date)),
        'day',
      ) &&
      quantity === currentReceiveActual.quantity
    ) {
      /* same data / no need to update to backend */
      return;
    }
    if (bin && inventoryNumber && expiryDate && quantity) {
      const newReceiveActuals = structuredClone(actuals);
      newReceiveActuals[index] = inputtedReceiveActual;
      setActuals(newReceiveActuals);
      debouncePostInboundTaskReceiveActual(inputtedReceiveActual);
    }
  };

  const goToPrevSKUHandler = (): void => {
    if (currentReceiveItemIndex === 0) {
      navigate(INBOUND_ROUTES.LIST);
      return;
    }
    // change redux index and inboundTaskReceiveItem
    dispatch(
      setInboundTaskcurrentReceiveItemIndex(currentReceiveItemIndex - 1),
    );
    dispatch(
      setInboundTaskReceiveItem(
        assignedInboundTaskItems[currentReceiveItemIndex - 1],
      ),
    );
  };

  const goToNextSKUHandler = (): void => {
    if (
      currentReceiveItemIndex === assignedInboundTaskItems.length - 1 ||
      assignedInboundTaskItems[currentReceiveItemIndex + 1].status !==
        InboundTaskItemStatusEnum.RECEIVING
    ) {
      navigate(INBOUND_ROUTES.LIST);
      return;
    }
    // change redux index and inboundTaskReceiveItem
    dispatch(
      setInboundTaskcurrentReceiveItemIndex(currentReceiveItemIndex + 1),
    );
    dispatch(
      setInboundTaskReceiveItem(
        assignedInboundTaskItems[currentReceiveItemIndex + 1],
      ),
    );
  };

  return (
    <>
      {/* Body */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextInboundTaskItemSKU">
            {inboundTaskReceiveItem.sku_code || ''}
          </S.Subtitle>
          <S.Subtitle item xs={12} data-testid="TextInboundTaskItemProductName">
            {inboundTaskReceiveItem.name || '-'}
          </S.Subtitle>
          <S.SubHeaderText
            item
            xs={12}
            data-testid="TextInboundTaskItemProductUOM"
          >
            {inboundTaskReceiveItem.uom || '-'}
          </S.SubHeaderText>
        </S.SubtitleWrapper>
        <S.HeaderContentWrapper container>
          <Grid item xs={6}>
            <S.ProgressText>
              ORDER RECEIVED:
              <S.ProgressSpan
                color={colorProgress}
                data-testid="TextInboundTaskItemOrderPicked"
              >
                {receivedQty}/{inboundTaskReceiveItem.quantity}
              </S.ProgressSpan>
            </S.ProgressText>
          </Grid>
        </S.HeaderContentWrapper>

        {/* main forms */}
        {actuals.map((actual, index) => (
          <ReceiveProcessForm
            index={index}
            item={actual}
            maxQuantityToAdd={
              inboundTaskReceiveItem.quantity -
              actuals.reduce((total, act) => {
                if (
                  act.bin !== actual.bin ||
                  act.inventory_number !== actual.inventory_number
                ) {
                  return total + act.quantity;
                }
                return total;
              }, 0)
            }
            onAllFilled={handleUpdateReceiveActual}
            verifyBin={handleVerifyBin}
            onRemove={handleRemoveReceiveActual}
          />
        ))}
        <Grid container style={{ marginTop: 8 }}>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.RedButtonText
              onClick={handleRemoveBatch}
              disabled={actuals.length === 1}
              data-testid="BtnInboundTaskItemRemoveBatch"
            >
              REMOVE BATCH
            </S.RedButtonText>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'right' }}>
            <S.BlueButtonText
              onClick={handleAddBatch}
              data-testid="BtnInboundTaskItemAddBatch"
            >
              <Add /> ADD BATCH
            </S.BlueButtonText>
          </Grid>
        </Grid>
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton onClick={goToPrevSKUHandler}>
              {currentReceiveItemIndex === 0
                ? 'BACK TO RECEIVE LIST'
                : 'PREV SKU'}
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton onClick={goToNextSKUHandler}>
              {currentReceiveItemIndex ===
                assignedInboundTaskItems.length - 1 ||
              assignedInboundTaskItems[currentReceiveItemIndex + 1].status !==
                InboundTaskItemStatusEnum.RECEIVING
                ? 'DONE'
                : 'NEXT SKU'}{' '}
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </>
  );
};
